import React, { Fragment, useEffect } from "react";
import { useOrder } from "../../contexts/order";
import { Dialog, Transition } from "@headlessui/react";
import { useFormik } from "formik";
import classNames from "classnames";
import { OrderType, OrderTypeTitle } from "../../enums";
import { basePath, basePathForStorage } from "../config";
import { toast } from "react-toastify";
import { useAuth } from "../../contexts/auth";
import { IoMdAddCircle } from "react-icons/io";
import { useSearchParams } from "react-router-dom";

const validate = (values, isDisabledForm) => {
  const errors = {};

  if (!values.paper_size) {
    errors.paper_size = "Required";
  }

  if (!values.daya_mtr) {
    errors.daya_mtr = "Required";
  }

  if (!values.core) {
    errors.core = "Required";
  }

  if (!values.paper_type) {
    errors.paper_type = "Required";
  }

  if (values.roll <= 0) {
    errors.roll = "Required";
  }

  if (!values.party_name) {
    errors.party_name = "Required";
  }

  return errors;
};

export default function OrderPaperForm() {
  const [
    { isHistoryPage, formState, orders, orderdata, limit },
    {
      setFormState,
      setOrders,
      setOrderData,
      setAddCoreFormOpen,
      setPaperSizeFormOpen,
      setPaperTypeFormOpen,
    },
  ] = useOrder();
  const [{ activeUser }] = useAuth();

  const NameOfOrder = OrderTypeTitle[formState?.type];
  const isOpen = !!(formState?.type == OrderType.Paper);
  const isEditMode = !!formState?.order;
  const isDisabledForm = !isNaN(formState?.order?.order_status)
    ? formState?.order?.order_status > 2
    : isEditMode;

  useEffect(() => {
    if (isEditMode && isOpen) {
      const {
        id,
        paper_size,
        core,
        daya_mtr,
        mtr_daya,
        paper_type,
        roll,
        color,
        party_name,
        brand_name,
        due_date,
        note,
      } = formState.order;
      formik.setFieldValue("id", id);
      formik.setFieldValue("paper_size", paper_size);
      formik.setFieldValue("daya_mtr", daya_mtr);
      formik.setFieldValue("mtr_daya", mtr_daya);
      formik.setFieldValue("core", core);
      formik.setFieldValue("roll", roll);
      formik.setFieldValue("party_name", party_name);
      formik.setFieldValue("brand_name", brand_name);
      formik.setFieldValue("due_date", due_date);
      formik.setFieldValue("note", note || "");
      formik.setFieldValue("color", color || "");

      formik.setFieldValue("paper_type_name", paper_type);
      formik.setFieldValue(
        "paper_type",
        orderdata?.papers.find(({ paper }) => paper === paper_type)?.id
      );
    }
  }, [isEditMode]);

  const [searchParams] = useSearchParams();

  const q_payload = {
    query_brand_name: searchParams.get("brand_name"),
    query_party_name: searchParams.get("party_name"),
    query_order_type: searchParams.get("order_type"),
    query_size: searchParams.get("size"),
    query_teeth: searchParams.get("teeth"),
  };

  async function doSubmit(payload) {
    const body = new FormData();

    for (const key in payload) {
      if (Object.hasOwnProperty.call(payload, key)) {
        const element = payload[key];
        body.append(key, element);
      }
    }

    for (const key in q_payload) {
      if (Object.hasOwnProperty.call(q_payload, key)) {
        const element = q_payload[key];
        if (element !== null) {
          body.append(key, element);
        }
      }
    }

    // return 0;

    try {
      const res = await fetch(
        basePath +
          (isEditMode ? "editPaperOrder" : "addPaperOrder") +
          `?isHistoryPage=${isHistoryPage}&&page=${orders?.current_page}&&limit=${limit}`,
        {
          method: "post",
          headers: {
            Authorization: `Bearer ${activeUser?.remember_token}`,
          },
          body: body,
        }
      );

      const data = await res.json();
      console.log({ data });
      if (res.status === 401) {
        formik.resetForm({ payload: payload });
        document.cookie =
          "user=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        toast.error(data.message);
        return 0;
      }

      if (data.status) {
        formik.resetForm();
        setFormState(null);
        setOrders(JSON.parse(data.data));
        setOrderData({
          labels: JSON.parse(data.labels),
          cores: JSON.parse(data.cores),
          ups: JSON.parse(data.ups),
          papers: JSON.parse(data.papers),
          colors: JSON.parse(data.colors),
          paper_size: JSON.parse(data.paper_size),
          teeths: data.teeths,
        });
        toast.success(data.message);
      } else {
        // setOrders(null)
        formik.resetForm({ payload: payload });
        toast.error("error: " + data.message);
        console.error("error: " + data.message);
      }
    } catch (error) {
      formik.resetForm({ payload: payload });
      toast.error("error: " + error);
      console.error("edit/addBarcode:: api calling fail =>", error);
    }
  }

  const formik = useFormik({
    initialValues: {
      id: "",
      paper_size: "",
      daya_mtr: "",
      mtr_daya: "mtr",
      core: "",

      paper_type: "",
      paper_type_name: "",

      color: "",

      roll: 0,

      party_name: "",
      brand_name: "",
      due_date: "",
      note: "",
      photo: "",
      order_type: formState?.type,
    },
    validate: (values) => validate(values, isDisabledForm),
    onSubmit: (values, action) => {
      doSubmit(values, action);
    },
  });

  function handlePaperType(e) {
    formik.setFieldValue(
      "paper_type_name",
      orderdata?.papers.find(({ id }) => id == e.target.value)?.paper
    );
  }

  function closeModal() {
    formik.resetForm({
      id: "",
      paper_size: "",
      daya_mtr: "",
      mtr_daya: "mtr",
      core: "",

      paper_type: "",
      paper_type_name: "",
      color: "",

      roll: 0,

      party_name: "",
      brand_name: "",
      due_date: "",
      note: "",
      photo: "",
    });
    setFormState(null);
  }

  if (!isOpen) return null;

  return (
    <Transition appear show={!!formState} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900"
                >
                  {isEditMode ? "Edit" : " Add"} {NameOfOrder}
                </Dialog.Title>
                <form onSubmit={formik.handleSubmit}>
                  <div className="mt-2 grid grid-cols-2 gap-4">
                    <div className="col-span-2 text-center">
                      <p className="text-orange text-bold">Add Order detail</p>
                    </div>

                    <input
                      type="hidden"
                      name="order_type"
                      value={formik.values.order_type}
                    />
                    <input
                      type="hidden"
                      name="paper_type_name"
                      value={formik.values.paper_type_name}
                    />
                    <input type="hidden" name="id" value={formik.values.id} />

                    <div>
                      <label className="block">
                        <span className="flex justify-between items-center text-sm font-medium text-slate-700 capitalize">
                          Paper Size
                          <button
                            type="button"
                            onClick={() => {
                              setPaperSizeFormOpen(true);
                            }}
                          >
                            <IoMdAddCircle fill="#ff4c3b" size={20} />
                          </button>
                        </span>
                        <select
                          name="paper_size"
                          onChange={formik.handleChange}
                          value={formik.values.paper_size}
                          disabled={isDisabledForm}
                          className={classNames(
                            "mt-1 block w-full px-3 py-2 bg-white border  rounded-md text-sm shadow-sm placeholder-slate-400 disabled:bg-slate-300",
                            formik.errors?.paper_size &&
                              formik.touched.paper_size &&
                              "border-red-600"
                          )}
                        >
                          <option value="">Select Papre Size</option>
                          {orderdata?.paper_size?.map(({ id, paper_size }) => (
                            <option key={id} value={paper_size}>
                              {paper_size}
                            </option>
                          ))}
                        </select>
                      </label>
                      {formik.errors.paper_size && formik.touched.paper_size ? (
                        <span className="text-sm text-red-600">
                          {formik.errors?.paper_size}
                        </span>
                      ) : null}
                    </div>

                    <div>
                      <span className="block text-sm font-medium text-slate-700 capitalize">
                        Mtr/Daya
                      </span>
                      <div className="flex">
                        <label className="block flex-grow">
                          <input
                            type="text"
                            name="daya_mtr"
                            // disabled={isDisabledForm}
                            onChange={formik.handleChange}
                            value={formik.values.daya_mtr}
                            className={classNames(
                              "mt-1 block w-full px-3 py-2 bg-white border  rounded-s-md text-sm shadow-sm placeholder-slate-400 disabled:bg-slate-300",
                              formik.errors?.daya_mtr &&
                                formik.touched.daya_mtr &&
                                "border-red-600"
                            )}
                          />
                        </label>
                        <select
                          name="mtr_daya"
                          onChange={formik.handleChange}
                          value={formik.values.mtr_daya}
                          // disabled={isDisabledForm}
                          className={classNames(
                            "mt-1 block px-3 py-2 bg-white border  rounded-e-md text-sm shadow-sm placeholder-slate-400 disabled:bg-slate-300",
                            formik.errors?.mtr_daya &&
                              formik.touched.mtr_daya &&
                              "border-red-600"
                          )}
                        >
                          <option value="mtr">mtr</option>
                          <option value="daya">daya</option>
                        </select>
                      </div>
                      {formik.errors.daya_mtr && formik.touched.daya_mtr ? (
                        <span className="text-sm text-red-600">
                          {formik.errors?.daya_mtr}
                        </span>
                      ) : null}
                    </div>

                    <div>
                      <label className="block">
                        <span className="flex justify-between items-center text-sm font-medium text-slate-700 capitalize">
                          select core
                          <button
                            type="button"
                            onClick={() => {
                              setAddCoreFormOpen(true);
                            }}
                          >
                            <IoMdAddCircle fill="#ff4c3b" size={20} />
                          </button>
                        </span>
                        <select
                          name="core"
                          onChange={formik.handleChange}
                          value={formik.values.core}
                          // disabled={isDisabledForm}
                          className={classNames(
                            "mt-1 block w-full px-3 py-2 bg-white border  rounded-md text-sm shadow-sm placeholder-slate-400 disabled:bg-slate-300",
                            formik.errors?.core &&
                              formik.touched.core &&
                              "border-red-600"
                          )}
                        >
                          <option value="">select Core</option>
                          {orderdata?.cores?.map(({ id, core }) => (
                            <option key={id} value={core}>
                              {core}
                            </option>
                          ))}
                        </select>
                      </label>
                      {formik.errors.core && formik.touched.core ? (
                        <span className="text-sm text-red-600">
                          {formik.errors?.core}
                        </span>
                      ) : null}
                    </div>

                    <div>
                      <label className="block">
                        <span className="flex justify-between items-center text-sm font-medium text-slate-700 capitalize">
                          select paper type
                          <button
                            type="button"
                            onClick={() => {
                              setPaperTypeFormOpen(true);
                            }}
                          >
                            <IoMdAddCircle fill="#ff4c3b" size={20} />
                          </button>
                        </span>

                        <select
                          name="paper_type"
                          // disabled={isDisabledForm}
                          onChange={(e) => {
                            formik.handleChange(e);
                            handlePaperType(e);
                          }}
                          value={formik.values.paper_type}
                          className={classNames(
                            "mt-1 block w-full px-3 py-2 bg-white border  rounded-md text-sm shadow-sm placeholder-slate-400 disabled:bg-slate-300",
                            formik.errors?.paper_type &&
                              formik.touched.paper_type &&
                              "border-red-600"
                          )}
                        >
                          <option value="">select paper type</option>
                          {orderdata?.papers?.map(({ id, paper }) => (
                            <option key={id} value={id}>
                              {paper}
                            </option>
                          ))}
                        </select>
                      </label>
                      {formik.errors.paper_type && formik.touched.paper_type ? (
                        <span className="text-sm text-red-600">
                          {formik.errors?.paper_type}
                        </span>
                      ) : null}
                    </div>

                    <div>
                      <label className="block">
                        <span className="block text-sm font-medium text-slate-700 capitalize">
                          select color
                        </span>
                        <select
                          name="color"
                          onChange={formik.handleChange}
                          value={formik.values.color}
                          className={classNames(
                            "mt-1 block w-full px-3 py-2 bg-white border  rounded-md text-sm shadow-sm placeholder-slate-400 disabled:bg-slate-300",
                            formik.errors?.color &&
                              formik.touched.color &&
                              "border-red-600"
                          )}
                        >
                          <option value="">select Color</option>
                          {orderdata?.colors?.map(({ id, color }) => (
                            <option key={id} value={color}>
                              {color}
                            </option>
                          ))}
                        </select>
                      </label>
                      {formik.errors.color && formik.touched.color ? (
                        <span className="text-sm text-red-600">
                          {formik.errors?.color}
                        </span>
                      ) : null}
                    </div>

                    <div className="col-span-2 text-center">
                      <p className="text-orange text-bold">
                        Order Quantity Detail
                      </p>
                    </div>

                    <div>
                      <label className="block">
                        <span className="block text-sm font-medium text-slate-700 capitalize">
                          quantity of roll
                        </span>
                        <input
                          type="number"
                          name="roll"
                          onChange={formik.handleChange}
                          value={formik.values.roll}
                          className={classNames(
                            "mt-1 block w-full px-3 py-2 bg-white border  rounded-md text-sm shadow-sm placeholder-slate-400 disabled:bg-slate-300",
                            formik.errors?.roll &&
                              formik.touched.roll &&
                              "border-red-600"
                          )}
                        />
                      </label>
                      {formik.errors.roll && formik.touched.roll ? (
                        <span className="text-sm text-red-600">
                          {formik.errors?.roll}
                        </span>
                      ) : null}
                    </div>

                    <div className="col-span-2 text-center">
                      <p className="text-orange text-bold">
                        Order Special Detail
                      </p>
                    </div>

                    <div>
                      <label className="block">
                        <span className="block text-sm font-medium text-slate-700 capitalize">
                          Party Name
                        </span>
                        <input
                          type="text"
                          name="party_name"
                          // disabled={isDisabledForm}
                          onChange={formik.handleChange}
                          value={formik.values.party_name}
                          className={classNames(
                            "mt-1 block w-full px-3 py-2 bg-white border  rounded-md text-sm shadow-sm placeholder-slate-400 disabled:bg-slate-300",
                            formik.errors?.party_name &&
                              formik.touched.party_name &&
                              "border-red-600"
                          )}
                        />
                      </label>
                      {formik.errors.party_name && formik.touched.party_name ? (
                        <span className="text-sm text-red-600">
                          {formik.errors?.party_name}
                        </span>
                      ) : null}
                    </div>

                    <div>
                      <label className="block">
                        <span className="block text-sm font-medium text-slate-700 capitalize">
                          Brand Name
                        </span>
                        <input
                          type="text"
                          name="brand_name"
                          // disabled={isDisabledForm}
                          onChange={formik.handleChange}
                          value={formik.values.brand_name}
                          className={classNames(
                            "mt-1 block w-full px-3 py-2 bg-white border  rounded-md text-sm shadow-sm placeholder-slate-400 disabled:bg-slate-300",
                            formik.errors?.brand_name &&
                              formik.touched.brand_name &&
                              "border-red-600"
                          )}
                        />
                      </label>
                      {formik.errors.brand_name && formik.touched.brand_name ? (
                        <span className="text-sm text-red-600">
                          {formik.errors?.brand_name}
                        </span>
                      ) : null}
                    </div>

                    <label className="block">
                      <span className="block text-sm font-medium text-slate-700 capitalize">
                        Due date
                      </span>
                      <input
                        type="date"
                        name="due_date"
                        // disabled={isDisabledForm}
                        onChange={formik.handleChange}
                        value={formik.values.due_date}
                        className="mt-1 block w-full px-3 py-2 bg-white border  rounded-md text-sm shadow-sm placeholder-slate-400 disabled:bg-slate-300"
                      />
                    </label>

                    <label className="block col-span-2">
                      <span className="block text-sm font-medium text-slate-700 capitalize">
                        notes
                      </span>
                      <textarea
                        name="note"
                        // disabled={isDisabledForm}
                        onChange={formik.handleChange}
                        value={formik.values.note}
                        className="mt-1 block w-full px-3 py-2 bg-white border  rounded-md text-sm shadow-sm placeholder-slate-400 disabled:bg-slate-300"
                        rows="5"
                      ></textarea>
                    </label>

                    <div className="col-span-2 text-center">
                      <p className="text-orange text-bold">Photo Attachment </p>
                    </div>

                    <div className="col-span-2 flex gap-x-4 items-center">
                      {formState?.order?.photo && (
                        <div className="max-w-sm">
                          <img
                            src={basePathForStorage + formState.order.photo}
                            alt="color order"
                          />
                        </div>
                      )}
                      <label className="flex-grow-1 w-full">
                        <span className="block text-sm font-medium text-slate-700 capitalize">
                          Choose Image
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          name="photo"
                          // disabled={isDisabledForm}
                          onChange={(event) => {
                            formik.setFieldValue(
                              "photo",
                              event.currentTarget.files[0]
                            );
                          }}
                          className="mt-1 block w-full px-3 py-2 bg-white border  rounded-md text-sm shadow-sm placeholder-slate-400 disabled:bg-slate-300"
                        />
                      </label>
                    </div>
                  </div>

                  <div className="mt-4 flex justify-end gap-3">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                      onClick={closeModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={formik.isSubmitting}
                      className={classNames(
                        "inline-flex justify-center rounded-md border border-transparent bg-green-100 px-4 py-2 text-sm font-medium text-green-900 hover:bg-green-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2",
                        formik.isSubmitting && "disabled:bg-slate-300"
                      )}
                    >
                      {isEditMode ? "Edit" : " Add"} Order ({NameOfOrder})
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
